<template>
  <FlashNotifications ref="flash" />

  <!-- ═══════════════ MOBILE ═══════════════ -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-app-screen">

    <!-- Mobile skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
          <div class="flex items-center px-4 h-14">
            <div class="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
        <div class="pb-20">
          <div class="px-4 pt-6 pb-2 h-2.5 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
            <div class="px-4 py-3.5 flex items-center justify-between gap-4">
              <div class="flex-1 space-y-1.5">
                <div class="h-3 w-44 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2.5 w-56 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
              <div class="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0"></div>
            </div>
          </div>
          <div class="px-4 pt-6 pb-2 h-2.5 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
            <div class="px-4 py-4 space-y-3">
              <div class="h-48 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              <div class="h-9 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg ml-auto"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Mobile loaded content -->
    <template v-else>
      <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center px-4 h-14">
          <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">Custom Policy</h1>
        </div>
      </div>

      <div class="pb-20">
        <!-- Visibility -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Visibility</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
          <div class="px-4 py-3.5 flex items-center justify-between gap-4">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug">{{ I18N.website_content.account_settings.show_custom_policy }}</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{{ I18N.website_content.account_settings.show_custom_policy_description }}</p>
            </div>
            <button type="button"
                    @click="toggleShowPolicy"
                    :class="useSettingsStoreObj().current_user.show_custom_policy ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'"
                    class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none">
              <span :class="useSettingsStoreObj().current_user.show_custom_policy ? 'translate-x-5' : 'translate-x-0.5'"
                    class="inline-block h-5 w-5 mt-0.5 transform rounded-full bg-white shadow transition-transform duration-200"/>
            </button>
          </div>
        </div>

        <!-- Versions -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Versions</p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
          <div class="px-4 py-3">
            <button @click="ClickNewPolicy"
                    class="w-full flex items-center justify-center gap-2 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              {{ I18N.website_content.account_settings.new_policy }}
            </button>
          </div>
          <div v-for="pv in useSettingsStoreObj().policies.slice().reverse()" :key="pv.id"
               @click="editPolicy(pv)"
               :class="policy.id === pv.id ? 'bg-blue-50 dark:bg-blue-900/10' : ''"
               class="px-4 py-3 flex items-center justify-between gap-3 cursor-pointer active:bg-gray-50 dark:active:bg-gray-800/50">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-900 dark:text-white leading-snug">{{ pv.version }}</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ pv.formatted_created_at }}</p>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0">
              <button v-if="pv.actionable && useSettingsStoreObj().policies.length > 1"
                      @click.stop="deletePolicy(pv.id)"
                      class="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                </svg>
              </button>
              <a :href="'/' + useSettingsStoreObj().current_user.url + '/policies/' + pv.id" target="_blank"
                 @click.stop
                 class="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <!-- Editor -->
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
          <span v-if="policy.id == null">New Policy</span>
          <span v-else>{{ I18N.website_content.account_settings.version }}: {{ policy.version }}</span>
        </p>
        <div v-if="policy.id != null && policy.actionable == false"
             class="mx-4 mb-3 flex items-start gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-2xl">
          <svg class="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
          </svg>
          <p class="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
            {{ I18N.website_content.account_settings.custom_banner_text1 }}
            <span class="font-bold">{{ policy.bookings_count }}</span>
            {{ I18N.website_content.account_settings.custom_banner_text2 }}
          </p>
        </div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
          <div class="px-4 py-4">
            <div :class="{'opacity-60 pointer-events-none': !policy.actionable}">
              <quill-editor :content="policy.content"
                            :enable="policy.actionable"
                            :opts="{height: '220px'}"
                            @update-content="updateContent"></quill-editor>
            </div>
            <div class="flex justify-end pt-3">
              <button v-show="policy.actionable"
                      @click="savePolicy"
                      :disabled="temp_content === ''"
                      class="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors">
                {{ I18N.website_content.account_settings.personal.save }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>

  <!-- ═══════════════ DESKTOP ═══════════════ -->
  <div class="hidden sm:block min-h-app-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- Desktop skeleton -->
    <template v-if="!useSettingsStoreObj().init_data_loaded">
      <div class="animate-pulse">
        <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 sticky top-14 z-10">
          <div class="max-w-5xl mx-auto px-6 flex items-center justify-between h-14">
            <div class="flex items-center gap-2.5">
              <div class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800"></div>
              <div class="space-y-1.5">
                <div class="h-3 w-28 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2.5 w-20 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="max-w-5xl mx-auto px-6 py-6">
          <div class="flex gap-5">
            <div class="flex-1 min-w-0 space-y-4">
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                  <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
                  <div class="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
                <div class="px-6 py-4 flex items-center justify-between">
                  <div class="space-y-1.5 flex-1 mr-6">
                    <div class="h-3.5 w-44 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div class="h-3 w-64 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  </div>
                  <div class="h-6 w-11 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0"></div>
                </div>
              </div>
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                  <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
                  <div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
                <div class="px-6 py-5 space-y-3">
                  <div class="h-56 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                  <div class="h-9 w-20 bg-gray-200 dark:bg-gray-700 rounded-lg ml-auto"></div>
                </div>
              </div>
            </div>
            <div class="w-64 flex-shrink-0">
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                  <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
                  <div class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
                <div class="px-5 py-4 space-y-2">
                  <div class="h-8 w-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                  <div v-for="i in 3" :key="i" class="h-10 w-full bg-gray-50 dark:bg-gray-800/50 rounded-lg"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Desktop loaded content -->
    <template v-else>
      <!-- Sub-page header -->
      <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 sticky top-14 z-10">
        <div class="max-w-5xl mx-auto px-6 flex items-center justify-between h-14">
          <div class="flex items-center gap-2.5">
            <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"/>
              </svg>
            </div>
            <div>
              <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Custom Policy</h1>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Public Profile</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="max-w-5xl mx-auto px-6 py-6">
        <div class="flex gap-5">

          <!-- Left column -->
          <div class="flex-1 min-w-0 space-y-4">

            <!-- Visibility card -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
                  </svg>
                </div>
                <div>
                  <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Visibility</h2>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Show your custom policy on your public pages</p>
                </div>
              </div>
              <div class="px-6 py-4 flex items-center justify-between gap-6">
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug">{{ I18N.website_content.account_settings.show_custom_policy }}</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{{ I18N.website_content.account_settings.show_custom_policy_description }}</p>
                </div>
                <button type="button"
                        @click="toggleShowPolicy"
                        :class="useSettingsStoreObj().current_user.show_custom_policy ? 'bg-blue-600 shadow-sm shadow-blue-500/30' : 'bg-gray-200 dark:bg-gray-700'"
                        class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2">
                  <span :class="useSettingsStoreObj().current_user.show_custom_policy ? 'translate-x-5' : 'translate-x-0.5'"
                        class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200"/>
                </button>
              </div>
            </div>

            <!-- Editor card -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
                <div class="flex items-center gap-3">
                  <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">
                      <span v-if="policy.id == null">{{ I18N.website_content.account_settings.new_policy }}</span>
                      <span v-else>{{ I18N.website_content.account_settings.version }}: {{ policy.version }}</span>
                    </h2>
                    <p v-if="policy.id != null" class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">
                      {{ I18N.website_content.account_settings.updated_on }} {{ policy.formatted_updated_at }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Warning banner -->
              <div v-if="policy.id != null && policy.actionable == false"
                   class="mx-6 mt-4 flex items-start gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-lg">
                <svg class="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
                </svg>
                <p class="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                  {{ I18N.website_content.account_settings.custom_banner_text1 }}
                  <span class="font-bold">{{ policy.bookings_count }}</span>
                  {{ I18N.website_content.account_settings.custom_banner_text2 }}
                </p>
              </div>

              <div class="px-6 py-5">
                <div :class="{'opacity-60 pointer-events-none': !policy.actionable}">
                  <quill-editor :content="policy.content"
                                :enable="policy.actionable"
                                :opts="{height: '260px'}"
                                @update-content="updateContent"></quill-editor>
                </div>
                <div class="flex justify-end pt-4">
                  <button v-show="policy.actionable"
                          @click="savePolicy"
                          :disabled="temp_content === ''"
                          class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors">
                    {{ I18N.website_content.account_settings.personal.save }}
                  </button>
                </div>
              </div>
            </div>

          </div>

          <!-- Right column: versions -->
          <div class="w-64 flex-shrink-0">
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden sticky top-[calc(3.5rem+3.5rem+1.5rem)]">
              <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z"/>
                  </svg>
                </div>
                <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">{{ I18N.website_content.account_settings.versions }}</h2>
              </div>

              <div class="px-4 py-3 border-b border-gray-50 dark:border-gray-800/60">
                <button @click="ClickNewPolicy"
                        class="w-full flex items-center justify-center gap-2 py-2 text-xs font-semibold text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                  </svg>
                  {{ I18N.website_content.account_settings.new_policy }}
                </button>
              </div>

              <div class="divide-y divide-gray-50 dark:divide-gray-800/60 max-h-80 overflow-y-auto">
                <div v-for="pv in useSettingsStoreObj().policies.slice().reverse()" :key="pv.id"
                     @click="editPolicy(pv)"
                     class="relative px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40"
                     :class="policy.id === pv.id ? 'bg-blue-50 dark:bg-blue-900/10' : ''">
                  <div v-if="policy.id === pv.id" class="absolute left-0 top-3 bottom-3 w-0.5 bg-blue-600 rounded-r"></div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-semibold text-gray-900 dark:text-white leading-snug truncate"
                       :class="policy.id === pv.id ? 'text-blue-600 dark:text-blue-400' : ''">
                      {{ pv.version }}
                    </p>
                    <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{{ pv.formatted_created_at }}</p>
                  </div>
                  <div class="flex items-center gap-1 flex-shrink-0">
                    <button v-if="pv.actionable && useSettingsStoreObj().policies.length > 1"
                            @click.stop="deletePolicy(pv.id)"
                            class="w-6 h-6 flex items-center justify-center rounded text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                      </svg>
                    </button>
                    <a :href="'/' + useSettingsStoreObj().current_user.url + '/policies/' + pv.id" target="_blank"
                       @click.stop
                       class="w-6 h-6 flex items-center justify-center rounded text-gray-300 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </template>
  </div>

  <!-- ═══════════════ DELETE MODAL ═══════════════ -->
  <Transition name="modal">
    <div v-if="showDeleteModal"
         class="fixed inset-0 z-50 flex items-center justify-center p-4"
         @click.self="toggleDeleteModal">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
      <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 w-full max-w-sm p-6 animate-in">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
            <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
            </svg>
          </div>
          <div>
            <h3 class="text-base font-bold text-gray-900 dark:text-white leading-snug">{{ I18N.website_content.account_settings.delete_policy }}</h3>
          </div>
        </div>
        <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-6">{{ I18N.website_content.account_settings.delete_policy_description }}</p>
        <div class="flex gap-3">
          <button @click="toggleDeleteModal"
                  class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
            {{ I18N.application.bookings.upcoming.cancel }}
          </button>
          <button @click="deletePolicyRequest"
                  class="flex-1 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors">
            {{ I18N.application.events.delete }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import { useSettingsStore } from 'controllers/vue_components/settings/v1/store'
import RequestMixin from 'controllers/vue_components/mixins/request'
import Constants from 'controllers/vue_components/mixins/constants'
import FlashNotifications from 'controllers/vue_components/flash_toast'

export default {
    name: 'CustomPolicy',
    props: [],
    mixins: [Constants, RequestMixin],
    components: { FlashNotifications },

    data() {
        return {
            showDeleteModal: false,
            deleteable_policy: null,
            temp_content: '',
            policy: {
                id: null,
                content: '',
                version: '',
                actionable: true
            },
            isDark: document.documentElement.classList.contains('dark'),
            isContentEdited: false,
        }
    },

    methods: {
        useSettingsStoreObj() {
            return useSettingsStore()
        },

        async toggleShowPolicy() {
            const newValue = !this.useSettingsStoreObj().current_user.show_custom_policy
            const response = await this.useSettingsStoreObj().updateCurrentUser({ user: { show_custom_policy: newValue } })
            if (response.js_error === false) {
                if (response.response.success) {
                    this.$refs.flash.show('Profile settings saved', 'notice')
                } else {
                    this.$refs.flash.show(response.response.message, 'alert')
                }
            } else {
                this.$refs.flash.show(response.error?.data?.message || 'Something went wrong', 'alert')
            }
        },

        editPolicy(policy) {
            if (!this.isContentEdited || confirm('Changes you made may not be saved. Are you sure you want to proceed?')) {
                this.policy = policy
                this.temp_content = policy.content
                if (this.isContentEdited) this.isContentEdited = false
            }
        },

        deletePolicy(policy_id) {
            this.deleteable_policy = policy_id
            this.showDeleteModal = true
        },

        toggleDeleteModal() {
            this.showDeleteModal = !this.showDeleteModal
        },

        async deletePolicyRequest() {
            const response = await this.axiosRequest('DELETE', `/api/v1/host/settings/policies/${this.deleteable_policy}`, {}, false)
            if (response.js_error === false && response.response.success) {
                this.$refs.flash.show(response.response.message, 'notice')
                await this.useSettingsStoreObj().fetchUserPolicies()
                this.toggleDeleteModal()
                if (this.deleteable_policy === this.policy.id) {
                    const policies = this.useSettingsStoreObj().policies
                    if (policies.length > 0) {
                        this.policy = policies[policies.length - 1]
                    } else {
                        this.clearText()
                    }
                }
            }
        },

        async ClickNewPolicy() {
            if (!this.isContentEdited || confirm('Changes you made may not be saved. Are you sure you want to proceed?')) {
                this.clearText()
                await new Promise(resolve => setTimeout(resolve, 100))
                this.clearText()
                if (this.isContentEdited) this.isContentEdited = false
            }
        },

        async savePolicy() {
            this.policy.content = this.temp_content
            const data = { policy: { content: this.policy.content } }
            let response
            if (this.policy.id == null) {
                response = await this.axiosRequest('POST', '/api/v1/host/settings/policies', data, false)
            } else {
                response = await this.axiosRequest('PUT', `/api/v1/host/settings/policies/${this.policy.id}`, data, false)
            }
            if (response.js_error === false && response.response.success) {
                this.$refs.flash.show(response.response.message, 'notice')
                await this.useSettingsStoreObj().fetchUserPolicies()
                const policies = this.useSettingsStoreObj().policies
                if (policies.length > 0) {
                    this.policy = policies[policies.length - 1]
                    this.temp_content = ''
                    this.isContentEdited = false
                } else {
                    this.clearText()
                }
            }
        },

        clearText() {
            this.policy = { id: null, content: '', version: '', actionable: true }
            this.temp_content = ''
            this.isContentEdited = false
        },

        updateContent(value) {
            this.temp_content = value
            if (this.policy.content !== this.temp_content) this.isContentEdited = true
        },

        handleNavigation(event) {
            if (this.isContentEdited) {
                const msg = 'Changes you made may not be saved. Are you sure you want to proceed?'
                event.returnValue = msg
                return msg
            }
        },
    },

    beforeRouteLeave(to, from, next) {
        if (this.isContentEdited) {
            if (confirm('Changes you made may not be saved. Are you sure you want to proceed?')) {
                next()
            } else {
                next(false)
            }
        } else {
            next()
        }
    },

    computed: {
        storePolicies() {
            return useSettingsStore().policies
        },
    },

    watch: {
        storePolicies: {
            immediate: true,
            handler(policies) {
                if (policies.length > 0 && this.policy.id == null) {
                    this.policy = { ...policies[policies.length - 1] }
                }
            },
        },
    },

    created() {
        window.addEventListener('beforeunload', this.handleNavigation)
    },

    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleNavigation)
    },
}
</script>

<style scoped>
.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .animate-in { transition: transform 0.2s ease; }
.modal-enter-from .animate-in { transform: scale(0.96) translateY(8px); }
</style>
